import { Card, CardActions, CardContent, Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import React from 'react';
import { CerebroIconButton, CerebroLabel, CerebroLink, CerebroText, LenderStoreDot } from '../../../shared/components';
import {
	CurrentUser,
	DataRoomUser,
	DataRoom,
	UnlockUser,
	AssociatedTeamMember,
	AvailableNdas,
	MilestoneReport,
} from '../../../shared/models';
import { DateUtils, EnumUtils } from '../../../shared/utils';
import { LenderParticipantActionButton } from './LenderParticipantActionButton';
import { TeamMemberRowItem } from './TeamMemberRowItem';
import { faNotes, faHandshake } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAvailableNdasQuery } from '../../../shared/hooks/Queries/useAvailableNdasQuery';

interface DataRoomUserItemProps {
	dataRoomGuid: string | undefined;
	currentUser: CurrentUser;
	dataRoom: DataRoom;
	user: DataRoomUser;
	tab: string;
	unlockUser: (user: UnlockUser) => void;
	accessLogs: (user: UnlockUser) => void;
	optOutUser: (user: DataRoomUser) => void;
	addNote: (user: DataRoomUser) => void;
	viewNotes: (user: DataRoomUser) => void;
	addTeamMember: (user: DataRoomUser) => void;
	removeTeamMember: (member: AssociatedTeamMember) => void;
	sendInvite: (user: UnlockUser) => void;
	sendEmail: (user: DataRoomUser) => void;
	viewEmails: (user: DataRoomUser) => void;
	uploadNda: (user: DataRoomUser) => void;
	grantAccess: (user: DataRoomUser) => void;
	deleteDataRoomUser: (user: DataRoomUser) => void;
	viewOptOut: (user: DataRoomUser) => void;
	contactDetails: (user: DataRoomUser) => void;
	teamMemberDetails: (user: AssociatedTeamMember) => void;
	progressDetails: (milestone: MilestoneReport | null) => void;
}

export const DataRoomUserItem: React.FC<DataRoomUserItemProps> = ({
	dataRoomGuid,
	currentUser,
	dataRoom,
	user,
	tab,
	unlockUser,
	accessLogs,
	optOutUser,
	addNote,
	viewNotes,
	addTeamMember,
	removeTeamMember,
	sendInvite,
	sendEmail,
	viewEmails,
	uploadNda,
	grantAccess,
	deleteDataRoomUser,
	viewOptOut,
	contactDetails,
	teamMemberDetails,
	progressDetails,
}) => {
	const [showTeamMembers, setShowTeamMembers] = React.useState<boolean>(false);

	const onToggleTeamMembers = () => {
		const tm = !showTeamMembers;
		setShowTeamMembers(tm);
	};

	const availableNdasQuery = useAvailableNdasQuery(dataRoomGuid, user.userGuid);

	return (
		<Grid key={tab + '-' + user.guid} item xs={12}>
			<Card
				elevation={3}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					flexDirection: 'column',
					height: '100%',
					marginBottom: 2,
					width: '1000px',
				}}
			>
				<CardContent sx={{ paddingBottom: '0 !important' }}>
					<Grid container sx={{ marginBottom: 2 }}>
						<Grid item xs={8}>
							{currentUser.isEmployeeUser && (
								<LenderStoreDot
									score={user.lenderScore}
									isFlagged={user.lenderScoreFlag}
									sx={{ marginRight: 0.5 }}
								/>
							)}
							<CerebroText variant="large" component="span" sx={{ fontWeight: 'bold' }}>
								{user.firstName} {user.lastName}
							</CerebroText>
							{user.noteCount > 0 && (
								<CerebroIconButton
									onClick={() => viewNotes(user)}
									sx={theme => ({ color: theme.palette.info.main })}
									title="Click to view notes"
								>
									<FontAwesomeIcon icon={faNotes} size="xs" />
								</CerebroIconButton>
							)}

							{availableNdasQuery.isSuccess && hasAvailableNda(availableNdasQuery.data) && (
								<CerebroIconButton
									disabled={true}
									sx={theme => ({ color: theme.palette.info.main })}
									title={getNdaTitle(availableNdasQuery.data)}
								>
									<FontAwesomeIcon icon={faHandshake} size="xs" />
								</CerebroIconButton>
							)}
						</Grid>
						<Grid item xs={4} sx={{ textAlign: 'right' }}>
							<CerebroLabel sx={{ fontWeight: 'bold' }}>{user.bankName}</CerebroLabel>
						</Grid>
					</Grid>
					<CerebroText gutterBottom sx={{ marginTop: 1 }}>
						{EnumUtils.GetStructureTrancheDescription(user.structureTranche)}
						{!user.structureTranche && <>Unknown Tranche</>}
					</CerebroText>
					<CerebroText gutterBottom sx={{ marginTop: 1 }}>
						{user.lastInviteSent && (
							<>Last invited: {DateUtils.FormatStringAsShortDate(user.lastInviteSent.toString())}</>
						)}
						{!user.lastInviteSent && <>Never invited</>}
					</CerebroText>
					{user.optedOut && (
						<CerebroLink sx={{ cursor: 'pointer', marginTop: 1 }} onClick={() => viewOptOut(user)}>
							Click for opt-out details
						</CerebroLink>
					)}

					{user.teamMembers?.length > 0 && (
						<>
							<br />
							<CerebroLink sx={{ cursor: 'pointer' }} onClick={onToggleTeamMembers}>
								{showTeamMembers
									? `Hide Team Members (${user.teamMembers.length})`
									: `Show Team Members (${user.teamMembers.length})`}
							</CerebroLink>

							{showTeamMembers && (
								<Table>
									<TableHead>
										<TableRow>
											<TableCell sx={{ minWidth: 200 }}>Name</TableCell>
											<TableCell sx={{ minWidth: 350 }}>Email</TableCell>
											<TableCell sx={{ minWidth: 250 }}>Last Accessed</TableCell>
											<TableCell></TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{user.teamMembers.map(member => {
											return (
												<TeamMemberRowItem
													key={tab + '-' + member.guid}
													dataRoomGuid={dataRoomGuid}
													dataRoom={dataRoom}
													user={user}
													member={member}
													currentUser={currentUser}
													unlockUser={unlockUser}
													accessLogs={accessLogs}
													removeTeamMember={removeTeamMember}
													sendInvite={sendInvite}
													contactDetails={teamMemberDetails}
												/>
											);
										})}
									</TableBody>
								</Table>
							)}
						</>
					)}
				</CardContent>
				<CardActions
					sx={theme => ({
						alignItems: 'flex-end',
						backgroundColor: theme.palette.gray.background,
						borderTop: `1px solid ${theme.palette.divider}`,
						justifyContent: 'right',
					})}
				>
					<Grid container>
						<Grid item xs={12} md={6}>
							<CerebroText gutterBottom sx={{ marginTop: 1 }}>
								{user.lastAccessedOn && (
									<>
										Last accessed:{' '}
										{DateUtils.FormatStringAsShortDate(user.lastAccessedOn.toString())}
									</>
								)}
								{!user.lastAccessedOn && <>Never accessed</>}
							</CerebroText>
						</Grid>
						<Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
							<LenderParticipantActionButton
								isEmployee={currentUser.isEmployeeUser}
								isBorrower={currentUser.isCompanyUser}
								isBanker={currentUser.isBankUser}
								dataRoom={dataRoom}
								user={user}
								milestone={null}
								unlockUser={unlockUser}
								accessLogs={accessLogs}
								optOutUser={optOutUser}
								addNote={addNote}
								viewNotes={viewNotes}
								addTeamMember={addTeamMember}
								sendInvite={sendInvite}
								viewEmails={viewEmails}
								sendEmail={sendEmail}
								uploadNda={uploadNda}
								grantAccess={grantAccess}
								deleteDataRoomUser={deleteDataRoomUser}
								contactDetails={contactDetails}
								progressDetails={progressDetails}
							/>
						</Grid>
					</Grid>
				</CardActions>
			</Card>
		</Grid>
	);
};

function hasAvailableNda(ndas: AvailableNdas) {
	return ndas.hasBankSpecificNda || ndas.hasDealSpecificNda || ndas.hasTemplateNda;
}

function getNdaTitle(ndas: AvailableNdas) {
	return ndas.hasDealSpecificNda ? 'Deal Specific Nda' : ndas.hasTemplateNda ? 'Template Nda' : 'Custom Nda';
}
